import type { Edge, Graph, Node } from '@antv/x6';

const INVOCATION_ZIGZAG_HALF = 20;
const INVOCATION_ZIGZAG_OFFSET = 6;
const PARENT_PADDING = 24;
const INSIDE_INSET = 14;

type EdgeDirection = 'top' | 'bottom' | 'left' | 'right';

type Bounds = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const nodeCenter = (node: Node) => {
  const bbox = node.getBBox();
  return {
    x: bbox.x + bbox.width / 2,
    y: bbox.y + bbox.height / 2,
  };
};

const parentBoundsWithPadding = (parent: Node, padding: number): Bounds => {
  const bbox = parent.getBBox();
  return {
    minX: bbox.x + padding,
    maxX: bbox.x + bbox.width - padding,
    minY: bbox.y + padding,
    maxY: bbox.y + bbox.height - padding,
  };
};

const getDirection = (from: { x: number; y: number }, to: { x: number; y: number }): EdgeDirection => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0 ? 'right' : 'left';
  }
  return dy >= 0 ? 'bottom' : 'top';
};

const getBoundaryPoint = (node: Node, dir: EdgeDirection) => {
  const pos = node.getPosition();
  const size = node.getSize();
  const cx = pos.x + size.width / 2;
  const cy = pos.y + size.height / 2;
  switch (dir) {
    case 'top':
      return { x: cx, y: pos.y };
    case 'bottom':
      return { x: cx, y: pos.y + size.height };
    case 'left':
      return { x: pos.x, y: cy };
    case 'right':
    default:
      return { x: pos.x + size.width, y: cy };
  }
};

const nudgePointInside = (point: { x: number; y: number }, dir: EdgeDirection, inset: number) => {
  switch (dir) {
    case 'left':
      return { x: point.x + inset, y: point.y };
    case 'right':
      return { x: point.x - inset, y: point.y };
    case 'top':
      return { x: point.x, y: point.y + inset };
    case 'bottom':
    default:
      return { x: point.x, y: point.y - inset };
  }
};

const getInvocationParentNode = (sourceNode: Node, targetNode: Node) => {
  const srcParent = sourceNode.getParent?.();
  const tgtParent = targetNode.getParent?.();
  const srcData = sourceNode.getData?.() || {};
  const tgtData = targetNode.getData?.() || {};

  if (srcParent && tgtParent && srcParent.id === tgtParent.id && srcParent.isNode?.()) {
    return srcParent as Node;
  }
  if (srcParent?.isNode?.() && tgtData.isParentCopy && srcParent.id === targetNode.id) {
    return targetNode;
  }
  if (tgtParent?.isNode?.() && srcData.isParentCopy && tgtParent.id === sourceNode.id) {
    return sourceNode;
  }
  return null;
};

const buildZigzagVertices = (
  start: { x: number; y: number },
  end: { x: number; y: number },
  bounds?: Bounds,
) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (!length) return [];
  const ux = dx / length;
  const uy = dy / length;
  const nx = -uy;
  const ny = ux;

  const midX = (start.x + end.x) / 2;
  const midY = (start.y + end.y) / 2;

  let p1 = {
    x: midX + ux * INVOCATION_ZIGZAG_HALF - nx * INVOCATION_ZIGZAG_OFFSET,
    y: midY + uy * INVOCATION_ZIGZAG_HALF - ny * INVOCATION_ZIGZAG_OFFSET,
  };
  let p2 = {
    x: midX - ux * INVOCATION_ZIGZAG_HALF + nx * INVOCATION_ZIGZAG_OFFSET,
    y: midY - uy * INVOCATION_ZIGZAG_HALF + ny * INVOCATION_ZIGZAG_OFFSET,
  };

  if (bounds) {
    p1 = {
      x: clamp(p1.x, bounds.minX, bounds.maxX),
      y: clamp(p1.y, bounds.minY, bounds.maxY),
    };
    p2 = {
      x: clamp(p2.x, bounds.minX, bounds.maxX),
      y: clamp(p2.y, bounds.minY, bounds.maxY),
    };
  }

  return [p1, p2];
};

export function applyInvocationZigzagToEdge(edge: Edge, graph: Graph): void {
  const data = edge.getData?.() || {};
  if (data.edgeSubtype !== 'invocation') return;

  const sourceId = edge.getSourceCellId?.();
  const targetId = edge.getTargetCellId?.();
  if (!sourceId || !targetId) return;

  const sourceNode = graph.getCellById(sourceId) as Node | null;
  const targetNode = graph.getCellById(targetId) as Node | null;
  if (!sourceNode || !targetNode || !sourceNode.isNode?.() || !targetNode.isNode?.()) return;

  const parentNode = getInvocationParentNode(sourceNode, targetNode);
  const bounds = parentNode ? parentBoundsWithPadding(parentNode, PARENT_PADDING) : undefined;

  const sourceCenter = nodeCenter(sourceNode);
  const targetCenter = nodeCenter(targetNode);
  const sourceDir = getDirection(sourceCenter, targetCenter);
  const targetDir = getDirection(targetCenter, sourceCenter);

  const sourceBoundary = getBoundaryPoint(sourceNode, sourceDir);
  const targetBoundary = getBoundaryPoint(targetNode, targetDir);

  let start = sourceBoundary;
  let end = targetBoundary;
  const startInside = nudgePointInside(sourceBoundary, sourceDir, INSIDE_INSET);
  const endInside = nudgePointInside(targetBoundary, targetDir, INSIDE_INSET);

  if (bounds) {
    start = {
      x: clamp(startInside.x, bounds.minX, bounds.maxX),
      y: clamp(startInside.y, bounds.minY, bounds.maxY),
    };
    end = {
      x: clamp(endInside.x, bounds.minX, bounds.maxX),
      y: clamp(endInside.y, bounds.minY, bounds.maxY),
    };
  }

  const zigzag = buildZigzagVertices(start, end, bounds);
  const vertices = bounds ? [start, ...zigzag, end] : zigzag;

  edge.setVertices(vertices);
  edge.setRouter({ name: 'normal' });
  edge.setConnector('normal');

  if (parentNode?.getZIndex && edge.setZIndex) {
    const parentZ = parentNode.getZIndex();
    if (typeof parentZ === 'number') {
      edge.setZIndex(parentZ + 1);
    }
  }
}

export function applyInvocationZigzag(graph: Graph): void {
  graph.getEdges().forEach(edge => applyInvocationZigzagToEdge(edge, graph));
}
